import { formatPrice } from './formatPrice';
import { groupBy, pick } from 'ramda';
import isEmptyArray from './isEmptyArray';
import { fetchFuelTransactionByRydOrderId } from '../redux/store/userData/fuelTransactions';
import { parseLocale } from './locale';
import { store } from '../redux/config';

const DASH = '-';

const isNil = (value: any) => value === null || value === undefined;

/* formatPrice renders null as "0 Ct" (null < 100), so amounts go through this instead. */
export const formatFuelAmount = (amount?: number | null): string =>
  isNil(amount) ? DASH : formatPrice(amount as number);

export const formatLitres = (quantity?: number | null): string => {
  if (isNil(quantity)) return DASH;

  const formatter = new Intl.NumberFormat(
    parseLocale(store.getState()?.userData?.user?.locale),
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    }
  );

  return `${formatter.format(quantity as number)} L`;
};

/* pricePerLitre already arrives in euros, unlike the cent amounts formatPrice handles. */
export const formatLitrePrice = (pricePerLitre?: number | null): string => {
  if (isNil(pricePerLitre)) return DASH;

  const formatter = new Intl.NumberFormat(
    parseLocale(store.getState()?.userData?.user?.locale),
    {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    }
  );

  return `${formatter.format(pricePerLitre as number)} / L`;
};

export const formatFuelLevel = (
  before?: number | null,
  after?: number | null
): string =>
  isNil(before) || isNil(after) ? DASH : `${before} % → ${after} %`;

const truncateCoordinate = (value: number, digits: number): string =>
  (Math.trunc(value * 10 ** digits) / 10 ** digits).toFixed(digits);

export const truncateGeoLocation = (geoLocation: any, digits = 3): string => {
  const [latitude, longitude] =
    typeof geoLocation === 'string'
      ? geoLocation.split(',').map(Number)
      : [geoLocation?.latitude, geoLocation?.longitude];
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return DASH;
  }
  return `${truncateCoordinate(latitude, digits)},${truncateCoordinate(
    longitude,
    digits
  )}`;
};

export const formatEventFuelLevel = (fuelLevel?: number | null): string =>
  `Fuel level ${isNil(fuelLevel) ? DASH : `${fuelLevel}%`}`;

export const FUEL_SESSION_MARGIN = 24 * 60 * 60 * 1000;

export const isUnresolvedRydEvent = (event: any) =>
  event?.type === 'rydFuelSessionStatusChanged' &&
  event.rydOrderId &&
  !event.fuelTransaction &&
  (!event.fuelTransactionId || event.fuelQuantity == null);

export const getEventFuelTransactionId = (event: any): string | undefined =>
  event?.fuelTransactionId ?? event?.fuelTransaction?.id;

// TODO: drop the lookup once the backend sets the fuel transaction fields on the event itself.
export const attachFuelTransactions = async (events: any[]) => {
  if (!Array.isArray(events)) {
    return events;
  }
  const rydOrderIds = [
    ...new Set(events.filter(isUnresolvedRydEvent).map((e) => e.rydOrderId)),
  ];
  if (isEmptyArray(rydOrderIds)) {
    return events;
  }
  const transactions = await Promise.all(
    rydOrderIds.map(fetchFuelTransactionByRydOrderId)
  );
  const transactionByRydOrderId = new Map(
    rydOrderIds.map((rydOrderId, i) => [rydOrderId, transactions[i]])
  );
  return events.map((event) => {
    const transaction: any = isUnresolvedRydEvent(event)
      ? transactionByRydOrderId.get(event.rydOrderId)
      : undefined;
    return transaction ? { ...event, fuelTransaction: transaction } : event;
  });
};

const FUEL_TRANSACTION_FIELDS = [
  'fuelTransactionId',
  'fuelTransaction',
  'fuelQuantity',
  'fuelProduct',
];

const ENDED_FUEL_SESSION_STATUSES = ['acquired', 'completed', 'closed'];

export const aggregateFuelSessions = (events: any[]) =>
  Object.values(groupBy((e: any) => e.fuelSessionId, events)).flatMap(
    (session: any[]) => {
      const byTime = [...session].sort(
        (a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime()
      );
      const first = byTime[0];
      const last = byTime[byTime.length - 1];
      const withTransaction = byTime.find(getEventFuelTransactionId);
      const shared = {
        fuelSessionId: first.fuelSessionId,
        tripId: first.tripId,
        ...pick(FUEL_TRANSACTION_FIELDS, withTransaction || {}),
      };
      const started = {
        ...shared,
        type: 'fuelSessionStarted',
        addedAt: first.addedAt,
        fuelLevel: first.fuelLevel,
        geoLocation: first.geoLocation,
      };
      const completed = byTime.find(
        (e) => e.newFuelSessionStatus === 'completed'
      );
      const ended = {
        ...shared,
        ...pick(
          ['fuelledAt', 'fuelledAmount', 'fuelStationBrand'],
          completed || {}
        ),
        addedAt: last.addedAt,
        fuelLevel: last.fuelLevel,
      };
      if (ENDED_FUEL_SESSION_STATUSES.includes(last.newFuelSessionStatus)) {
        return [
          started,
          {
            ...ended,
            type: 'fuelSessionEnded',
            newFuelSessionStatus: last.newFuelSessionStatus,
          },
        ];
      }
      return [started];
    }
  );
